import React from "react";
import { Avatar, Badge, Dropdown, Button, Typography } from "antd";
import {
  BellOutlined,
  UserOutlined, // ← was missing, now added
  LogoutOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
} from "@ant-design/icons";
import useAuth from "@/hooks/useAuth";
import { ArrowLeftIcon, ReportAnalyticsIcon } from "@/asset/icons";
import { useDispatch } from "react-redux";
import { logout } from "@/redux/features/authentication";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useGetCompanyQuery } from "@/services/api/company/companyApi";
import { Box } from "@mui/material";

const { Title } = Typography;

const Navbar = ({ collapsed, toggleSidebar, isMobile }) => {
  const user = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: companyData } = useGetCompanyQuery();
  const companyName = companyData?.[0]?.name || "Sundry Food Services";
  const companyLogo = companyData?.[0]?.url;

  const getPageTitle = () => {
    const path = location.pathname;

    if (path === "/admin/dashboard" || path === "/admin") {
      return {
        title: `Welcome back, ${user.first_name}`,
        subtitle: "Here's what's happening in your store today 😊",
      };
    }

    if (path === "/admin/revenue") {
      return {
        title: `Revenue`,
        subtitle: "Track your revenue performance across different channels",
      };
    }

    // Profile page title
    if (path === "/admin/profile") {
      return {
        title: "My Profile",
        subtitle: "Manage your personal information",
      };
    }

    const segments = path.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    const formatted = lastSegment
      .replace(/-/g, " ")
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return {
      title: formatted,
      subtitle: null,
    };
  };

  const { title, subtitle } = getPageTitle();

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === "logout") {
      dispatch(logout());
      navigate("/admin-auth");
    }
    if (key === "profile") {
      navigate("/admin/profile"); // ← navigates to the profile page
    }
  };

  return (
    <header className="bg-white max-h-16 h-16 border-b border-gray-100 px-4 flex items-center justify-between sticky top-0 z-40">
      {/* LEFT SECTION */}
      <div className="flex items-center gap-3 relative">
        <Button
          type="text"
          icon={
            isMobile ? (
              collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )
            ) : collapsed ? (
              <MenuFoldOutlined />
            ) : (
              <ArrowLeftIcon />
            )
          }
          onClick={toggleSidebar}
          className={`
            bg-white border border-gray-200 shadow-sm hover:bg-gray-50
            ${
              isMobile || collapsed
                ? "rounded-md relative"
                : "rounded-full absolute -left-10 top-7"
            }
          `}
          size="small"
          style={{
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        />

        {/* LOGO - shows when sidebar is COLLAPSED */}
        {collapsed && (
          <div className="flex gap-2 items-center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <div className="hidden sm:block">
              <h2 className="text-sm font-semibold text-gray-900 mb-0 leading-tight">
                {companyName}
              </h2>
              <p className="text-xs text-gray-500 font-semibold leading-tight">
                {user.roles.map((role) => role.name).join(", ")}
              </p>
            </div>
          </div>
        )}

        {/* DYNAMIC PAGE TITLE - shows when sidebar is OPEN on desktop */}
        {!collapsed && !isMobile && title && (
          <div className="ml-0">
            <Title
              level={5}
              className="text-sm font-semibold text-gray-900 mb-0"
            >
              {title}
            </Title>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        )}
      </div>

      {/* RIGHT SECTION */}
      <div className="flex items-center space-x-2 md:space-x-4">
        <Link to="/admin/analytics" className="no-underline">
          <Button
            type="default"
            icon={<ReportAnalyticsIcon />}
            className="hidden lg:flex items-center border-gray-100"
          >
            <span>View Reporting & Analytics</span>
          </Button>
        </Link>

        <Dropdown
          menu={{ items: userMenuItems, onClick: handleMenuClick }}
          placement="bottomRight"
          trigger={["click"]}
        >
          <div className="cursor-pointer flex items-center gap-2">
            <Avatar
              size={32}
              src={user?.avatar}
              icon={!user?.avatar && <UserOutlined />}
              className="border-2 border-orange-200 bg-orange-50 text-orange-500"
            />
          </div>
        </Dropdown>
      </div>
    </header>
  );
};

export default Navbar;
